import {
  faChevronLeft,
  faChevronRight,
  faChevronsLeft,
  faChevronsRight,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import moment from 'moment';
import * as React from 'react';
import Calendar, {
  CalendarProps,
  OnChangeDateCallback,
  OnChangeDateRangeCallback,
} from 'react-calendar';
import Icon from '../../atoms/Icon';

import 'react-calendar/dist/Calendar.css';
import styles from './DatePicker.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'>,
    Pick<CalendarProps, 'view' | 'maxDetail'> {
  isRangePicker?: boolean;
  from: string;
  to: string;
  onChange: (args: OnChangeDateProps) => void;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  calendarClassName?: string;
  showNavigation?: boolean;
}

export interface OnChangeDateProps {
  from: string;
  to: string;
  presetName: string;
}

type CalendarViews = 'month' | 'year' | 'decade' | 'century';

const DatePicker: React.FunctionComponent<Props> = ({
  className,
  from,
  to,
  isRangePicker,
  showNavigation = true,
  onChange,
  minDate,
  maxDate,
  view,
  maxDetail,
  calendarClassName,
  ...htmlElementProps
}) => {
  const [currentView, setCurrentView] = React.useState<CalendarViews>(
    view || 'month',
  );
  const [manualDate, setManualDate] = React.useState<OnChangeDateProps | null>(
    null,
  );
  const startDate = moment(from).toDate();
  const endDate = moment(to).toDate();

  /**
   * Handles date selection using the DatePicker
   */
  const handleDateSelector = React.useCallback<
    NonNullable<OnChangeDateCallback | OnChangeDateRangeCallback>
  >(
    (date: Date | Date[]) => {
      if (isRangePicker) {
        const [start, end] = date as Date[];
        const dateProps = {
          from: moment(start).startOf('day').toISOString(),
          to: moment(end).endOf('day').toISOString(),
          presetName: '',
        };
        onChange(dateProps);
        setManualDate(dateProps);
      } else {
        const dateProps = {
          from: moment(date as Date)
            .startOf('day')
            .toISOString(),
          to: moment(date as Date)
            .endOf('day')
            .toISOString(),
          presetName: '',
        };
        onChange(dateProps);
        setManualDate(dateProps);
      }
    },
    [isRangePicker, onChange],
  );

  React.useEffect(() => {
    if (from !== manualDate?.from || to !== manualDate.to) {
      setManualDate(null);
    }
  }, [from, to, manualDate]);

  /** Sync view state */
  React.useEffect(() => {
    if (view && currentView !== view) {
      setCurrentView(view);
    }
  }, [currentView, view]);

  return (
    <div
      className={classNames(styles.wrapper, className)}
      {...htmlElementProps}
    >
      <Calendar
        className={classNames(styles.calendar, calendarClassName)}
        calendarType="US"
        tileClassName={classNames(styles['calendar-titles'], currentView)}
        minDate={minDate}
        maxDate={maxDate}
        onDrillUp={({ view: calendarView }) => setCurrentView(calendarView)}
        onDrillDown={({ view: calendarView }) => setCurrentView(calendarView)}
        activeStartDate={manualDate ? undefined : startDate}
        onActiveStartDateChange={() => {
          setManualDate({ from, to, presetName: '' });
        }}
        value={isRangePicker ? [startDate, endDate] : startDate}
        onChange={handleDateSelector}
        selectRange={isRangePicker}
        showNavigation={showNavigation}
        prevLabel={<Icon icon={faChevronLeft} color="primary" />}
        prev2Label={<Icon icon={faChevronsLeft} color="primary" />}
        nextLabel={<Icon icon={faChevronRight} color="primary" />}
        next2Label={<Icon icon={faChevronsRight} color="primary" />}
        view={view}
        maxDetail={maxDetail}
      />
    </div>
  );
};

export default DatePicker;
